<template>
    <!-- Render in a separate html file is incorrect -->
    <div>
        <DynamicTitle
            :elements="[{ name: 'Blacklist', selected: true }]"
            title="Configuration" />

        <v-data-table-server
            :headers="headers"
            :items="paginationData.items"
            :items-per-page-options="[10, 20, 50]"
            v-model:items-per-page="itemsPerPage"
            v-model:page="page"
            v-model:sort-by="sortBy"
            :loading="isLoading"
            :items-length="paginationData.totalCount"
            @update:options="getDataFromApi">
            <template v-slot:item.email="{ item }">
                <div>{{ item.email }}</div>
            </template>
            <template v-slot:item.firstName="{ item }">
                <div>{{ item.firstName }}</div>
            </template>
            <template v-slot:item.lastName="{ item }">
                <div>{{ item.lastName }}</div>
            </template>
            <template v-slot:item.lastModifiedDateTime="{ item }">
                <div>
                    {{
                        item.lastModifiedDateTime
                            ? getFormattedDateString(
                                  item.lastModifiedDateTime,
                                  true
                              )
                            : ""
                    }}
                </div>
            </template>

            <template v-slot:item.actions="{ item }">
                <div>
                    <v-btn
                        variant="plain"
                        icon="mdi-pencil"
                        class="mr-2"
                        @click="editItem(item)"></v-btn>
                    <v-btn
                        variant="plain"
                        icon="mdi-delete"
                        @click="deleteItem(item)"></v-btn>
                </div>
            </template>
        </v-data-table-server>

        <v-btn color="primary" @click="createDialog = true">
            Add new entry
        </v-btn>

        <BlacklistUpsetDialog
            :blacklist-entry-id="selectedItemId"
            v-model:visible="updateDialog"
            v-on:cancel="cancel" />
        <BlacklistUpsetDialog
            v-model:visible="createDialog"
            v-on:cancel="cancel" />
        <ConfirmationDialog
            v-model:visible="deleteDialog"
            title="Delete blacklist entry"
            @confirm="confirmDeleteBlacklist" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import DynamicTitle from "@/components/DynamicTitle";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import { blacklistModule } from "@/store/modules/blacklist";
import { BlacklistGetters } from "@/store/modules/blacklist/getters";
import { BlacklistActions } from "@/store/modules/blacklist/actions";
import {
    BlacklistFilter,
    SortParams
} from "@/models/blacklist/BlacklistFilter";
import { Blacklist } from "@/models/blacklist/Blacklist";
import BlacklistUpsetDialog from "@/components/BlacklistUpsetDialog";
import { PaginationResult } from "@/models/PaginationResult";
import { DataTableHeader } from "vuetify/lib/types";
import { SortItem } from "vuetify/lib/components/VDataTable/composables/sort";

export default defineComponent({
    components: {
        DynamicTitle,
        ConfirmationDialog,
        BlacklistUpsetDialog
    },
    computed: {
        ...blacklistModule.mapGetters({
            paginationDataGetter: BlacklistGetters.PaginationData,
            isLoading: BlacklistGetters.IsLoading
        }),
        paginationData(): PaginationResult<Blacklist> {
            return this.paginationDataGetter as PaginationResult<Blacklist>;
        }
    },
    methods: {
        ...blacklistModule.mapActions({
            loadFilteredBlacklist:
                BlacklistActions.LoadBlacklistsWithPagination,
            deleteBlacklist: BlacklistActions.DeleteBlacklist
        }),
        editItem(blacklistModel: Blacklist): void {
            if (blacklistModel.blacklistId) {
                this.selectedItemId = blacklistModel.blacklistId;
                this.updateDialog = true;
            }
        },
        deleteItem(blacklistModel: Blacklist): void {
            this.selectedItemId = blacklistModel.blacklistId;
            this.deleteDialog = true;
        },
        confirmDeleteBlacklist(): void {
            if (this.selectedItemId) {
                this.deleteBlacklist(this.selectedItemId);
            }
            this.selectedItemId = 0;
        },
        getDataFromApi(): void {
            const { page, itemsPerPage, sortBy } = this;

            const newSort = {} as Partial<SortParams>;
            sortBy.forEach((i) => {
                newSort[i.key as keyof SortParams] =
                    i.order == "asc" || i.order == true;
            });
            this.blacklistFilter.sortBy = newSort;

            if (!page || !itemsPerPage) {
                this.loadFilteredBlacklist({
                    paginationInfo: { take: 20, skip: 0 },
                    blacklistFilter: this.blacklistFilter
                });
                return;
            }

            this.loadFilteredBlacklist({
                paginationInfo: {
                    skip: (page - 1) * itemsPerPage,
                    take: itemsPerPage
                },
                blacklistFilter: this.blacklistFilter
            });
        },
        cancel(): void {
            this.selectedItemId = 0;
        },
        getFormattedDateString(dateString: string, showTime?: boolean): string {
            if (!showTime) {
                return new Date(dateString).toLocaleDateString("de-DE", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit"
                });
            } else {
                return new Date(dateString).toLocaleString("de-DE", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    second: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit"
                });
            }
        }
    },
    data() {
        return {
            headers: [
                { title: "Email", value: "email", sortable: true },
                { title: "FirstName", value: "firstName", sortable: true },
                { title: "LastName", value: "lastName", sortable: true },
                {
                    title: "Bearbeitet ",
                    value: "lastModifiedDateTime",
                    sortable: true
                },
                {
                    title: "Actions",
                    value: "actions",
                    sortable: false
                }
            ] as DataTableHeader[],
            page: 1,
            itemsPerPage: 20,
            sortBy: [] as SortItem[],
            deleteDialog: false,
            createDialog: false,
            updateDialog: false,
            blacklistFilter: {
                sortBy: {}
            } as BlacklistFilter,
            selectedItemId: 0 as number
        };
    }
});
</script>
